import { useNavigate } from 'react-router-dom'
import { Row, Col, Card } from 'react-bootstrap'
import { DcsPermissions, GridCellEnum, ICustomColumn, IDateSearchLocalStorageNames, IDealerChangedEvent, IDropDownValue, IGridLocalStorageNames, IRootState, TypeEnum } from 'types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { FormElement, Field, Form, FormClassComponent, FormRenderProps } from '@progress/kendo-react-form'
import { AddButton, FormDropDownList, FormSwitch, FormTextArea, PageHeader } from 'components'
import SAGrid, { GetDataResponse, GridGetDataProps, ISAGridRef } from 'components/Grid/SAGrid'
import { V1ApiCasesClient, V1ApiUsersClient } from 'core/http-common'
import { DeletedStateEnum, UsersDto } from '@v1UsersApi'
import toast from 'core/toast'
import { ComboBoxChangeEvent, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns'
import ConfirmModal from 'components/Modals/ConfirmModal'
import { BulkIdsDto, BulkUpdateCases, CaseReasonDto, CaseStatusDto, CaseSubReasonDto, CaseSubStatusDto, CasesViewDto, CaseViewEnum } from '@v1CasesApi'
import { endLoadingAction, startLoadingAction } from 'state/actions/appActions'
import { hasPermission } from 'state/reduxHelpers'
import { DEFAULT_PERMISSION_MESSAGE } from 'core/consts'
import './CasesGrid.css'
import useQueryParam from 'core/hooks/useQueryParam'
import FormTypeComboBox from 'components/Form/FormTypeComboBox'
import { RequiredValidator } from 'core/Validators'
import TypeComboBox, { ITypeComboBoxRef } from 'components/ComboBox/TypeComboBox'
import FormCustomField from 'components/Form/FormCustomField'
import { GridCellProps } from '@progress/kendo-react-grid'
import { numberToCurrency } from 'core/helpers'
import moment from 'moment'
import DealerSelect from 'components/Dealers/DealerSelect'


interface IProps {
    dealerId?: number | undefined
    showOpenCases?: boolean | undefined
    showClosedCases?: boolean | undefined
    title?: string
    customColumns?: ICustomColumn[] | undefined
    forDealerEditPage?: boolean,
    myCases?: boolean,
    hideCaseActions?: boolean,
    useSimpleSearch?: boolean,
    resizable?: boolean
}

interface OpenWindowButtonProps {
    url: string;
    label?: string;
}

interface IQueryParams {
    caseView?: string
    closedCasesByUser?: string
    closedCasesStartDate?: string
    closedCasesUser?: string
}

const CasesGrid = ({ dealerId, showClosedCases, showOpenCases, customColumns, title, forDealerEditPage, myCases, hideCaseActions, useSimpleSearch }: IProps) => {
    const navigate = useNavigate()
    const { caseView, closedCasesByUser, closedCasesStartDate, closedCasesUser } = useQueryParam<IQueryParams>()
    const [caseViewFromQueryLoaded, setCaseViewFromQueryLoaded] = useState(false)

    const gridRef = useRef<ISAGridRef>(null)
    const caseViewFormRef = useRef<FormClassComponent>(null)
    const caseFormRef = useRef<FormClassComponent>(null)
    const loading = useSelector((state: IRootState) => state.app.loading)

    const [caseUserOptions, setCaseUserOptions] = useState<IDropDownValue[]>([])
    const [statusOptions, setStatusOptions] = useState<IDropDownValue[]>([])
    const [subStatusOptions, setSubStatusOptions] = useState<IDropDownValue[]>([])
    const [reasonOptions, setReasonOptions] = useState<IDropDownValue[] | undefined>(undefined)
    const [subReasonOptions, setSubReasonOptions] = useState<IDropDownValue[] | undefined>(undefined)

    const [showNoteCol, setShowNoteCol] = useState<boolean | undefined>(true)
    const [showRoleCol, setShowRoleCol] = useState<boolean | undefined>(true)

    const [showCaseModal, setShowCaseModal] = useState(false)
    const [caseModalErrors, setCaseModalErrors] = useState<Array<string>>([])
    const [caseModalLoading, setCaseModalLoading] = useState(false)

    const [selectedIds, setSelectedIds] = useState<Array<number>>([])
    const [bulkUpdateCasesDto, setBulkUpdateCasesDto] = useState<BulkUpdateCases>()
    const [bulkSelectedDealerId, setBulkSelectedDealerId] = useState<number | undefined>(undefined)
    const [loaded, setLoaded] = useState(false)

    interface ICASES_LOCAL_STORAGE_KEYS {
        ALLCASES: IGridLocalStorageNames
        CSCASES: IGridLocalStorageNames
        CSCAMPAIGNS: IGridLocalStorageNames
        DATACASES: IGridLocalStorageNames
        RETENTIONCASES: IGridLocalStorageNames
        WEBCASES: IGridLocalStorageNames
        WEBSETUPCASES: IGridLocalStorageNames
        DOMAINCASES: IGridLocalStorageNames
        DBACCESSCASES: IGridLocalStorageNames
        SEOCASES: IGridLocalStorageNames
        CSESCALATIONS: IGridLocalStorageNames
        SALESCASES: IGridLocalStorageNames
        QACASES: IGridLocalStorageNames
        DEALERS: IGridLocalStorageNames
        ACCOUNTINGCASES: IGridLocalStorageNames
        LAZARUSCASES: IGridLocalStorageNames
        FRAZERONECASES: IGridLocalStorageNames
    }
    const LOCAL_STORAGE_KEYS: ICASES_LOCAL_STORAGE_KEYS = {
        ALLCASES: {
            sort: 'ALLCASESGridSort',
            search: 'ALLCASESGridSearch',
            pageSize: 'ALLCASESGridPageSize',
            selectedPage: 'ALLCASESGridSelectedPage'
        },
        CSCASES: {
            sort: 'CSCasesGridSort',
            search: 'CSCasesGridSearch',
            pageSize: 'CSCasesGridPageSize',
            selectedPage: 'CSCasesGridSelectedPage'
        },
        DATACASES: {
            sort: 'DATACASESGridSort',
            search: 'DATACASESGridSearch',
            pageSize: 'DATACASESGridPageSize',
            selectedPage: 'DATACASESGridSelectedPage'
        },
        RETENTIONCASES: {
            sort: 'RETENTIONCASESGridSort',
            search: 'RETENTIONCASESGridSearch',
            pageSize: 'RETENTIONCASESGridPageSize',
            selectedPage: 'RETENTIONCASESGridSelectedPage'
        },
        WEBCASES: {
            sort: 'WEBCASESGridSort',
            search: 'WEBCASESGridSearch',
            pageSize: 'WEBCASESGridPageSize',
            selectedPage: 'WEBCASESGridSelectedPage'
        },
        WEBSETUPCASES: {
            sort: 'WEBSETUPCASESGridSort',
            search: 'WEBSETUPCASESGridSearch',
            pageSize: 'WEBSETUPCASESGridPageSize',
            selectedPage: 'WEBSETUPCASESGridSelectedPage'
        },
        DOMAINCASES: {
            sort: 'DOMAINCASESGridSort',
            search: 'DOMAINCASESGridSearch',
            pageSize: 'DOMAINCASESGridPageSize',
            selectedPage: 'DOMAINCASESGridSelectedPage'
        },
        CSCAMPAIGNS: {
            sort: 'CSCAMPAIGNSGridSort',
            search: 'CSCAMPAIGNSGridSearch',
            pageSize: 'CSCAMPAIGNSGridPageSize',
            selectedPage: 'CSCAMPAIGNSGridSelectedPage'
        },
        DBACCESSCASES: {
            sort: 'DBACCESSGridSort',
            search: 'DBACCESSGridSearch',
            pageSize: 'DBACCESSGridPageSize',
            selectedPage: 'DBACCESSGridSelectedPage'
        },
        SEOCASES: {
            sort: 'SEOCASESGridSort',
            search: 'SEOCASESGridSearch',
            pageSize: 'SEOCASESGridPageSize',
            selectedPage: 'SEOCASESGridSelectedPage'
        },
        CSESCALATIONS: {
            sort: 'CSESCALATIONSGridSort',
            search: 'CSESCALATIONSGridSearch',
            pageSize: 'CSESCALATIONSGridPageSize',
            selectedPage: 'CSESCALATIONSGridSelectedPage'
        },
        SALESCASES: {
            sort: 'SALESCASESGridSort',
            search: 'SALESCASESGridSearch',
            pageSize: 'SALESCASESGridPageSize',
            selectedPage: 'SALESCASESGridSelectedPage'
        },
        QACASES: {
            sort: 'QACASESGridSort',
            search: 'QACASESGridSearch',
            pageSize: 'QACASESGridPageSize',
            selectedPage: 'QACASESGridSelectedPage'
        },

        DEALERS: {
            sort: 'dealerCaseGridSort',
            search: 'dealerCaseGridSearch',
            pageSize: 'dealerCaseGridPageSize',
            selectedPage: 'dealerCaseGridSelectedPage'
        },
        ACCOUNTINGCASES: {
            sort: 'ACCOUNTINGCASESGridSort',
            search: 'ACCOUNTINGCASESGridSearch',
            pageSize: 'ACCOUNTINGCASESGridPageSize',
            selectedPage: 'ACCOUNTINGCASESGridSelectedPage'
        },
        LAZARUSCASES: {
            sort: 'LAZARUSCASESGridSort',
            search: 'LAZARUSCASESGridSearch',
            pageSize: 'LAZARUSCASESGridPageSize',
            selectedPage: 'LAZARUSCASESGridSelectedPage'
        },
        FRAZERONECASES: {
            sort: 'FRAZERONECASESGridSort',
            search: 'FRAZERONECASESGridSearch',
            pageSize: 'FRAZERONECASESGridPageSize',
            selectedPage: 'FRAZERONECASESGridSelectedPage'
        },
    }


    let caseViews = [
        { text: 'All Cases', id: 1 },
        { text: 'CS Cases', id: 2 },
        { text: 'Data Cases', id: 3 },
        { text: 'Retention Cases', id: 4 },
        { text: 'Web Cases', id: 5 },
        { text: 'Web Setup Cases', id: 16 },
        { text: 'Domain Cases', id: 7 },
        { text: 'CS Campaigns', id: 8 },
        { text: 'DB Access Cases', id: 9 },
        { text: 'SEO Cases', id: 10 },
        { text: 'CS Escalations', id: 11 },
        { text: 'Sales Cases', id: 12 },
        { text: 'QA Cases', id: 13 },
        { text: 'Accounting Cases', id: 14 },
        { text: 'Lazarus Cases', id: 15 },
        { text: 'FrazerOne Cases', id: 16 },
    ]

    const defaultValues = {
        caseView: caseView && caseView.length > 0 ? caseViews.find(x => x.text.replace(/\s/g, '') == caseView) : caseViews.find(x => x.text === 'All Cases'),
    }

    const [casesViewType, setCasesViewType] = useState({
        value: { text: defaultValues.caseView?.text ?? 'All Cases', id: defaultValues.caseView?.id ?? 1 }
    })

    let caseViewFromQueryOrFilter = caseView && !caseViewFromQueryLoaded ? caseView : casesViewType.value.text
    let selectedCaseView = getCaseViewVal(caseViewFromQueryOrFilter)

    const [gridLocalStorageNames, setGridLocalStorageNames] = useState<IGridLocalStorageNames | undefined>({
        sort: forDealerEditPage ? LOCAL_STORAGE_KEYS.DEALERS.sort : LOCAL_STORAGE_KEYS[selectedCaseView.toLocaleUpperCase() as keyof ICASES_LOCAL_STORAGE_KEYS].sort,
        search: forDealerEditPage ? LOCAL_STORAGE_KEYS.DEALERS.search : LOCAL_STORAGE_KEYS[selectedCaseView.toLocaleUpperCase() as keyof ICASES_LOCAL_STORAGE_KEYS].search,
        selectedPage: forDealerEditPage ? LOCAL_STORAGE_KEYS.DEALERS.selectedPage : LOCAL_STORAGE_KEYS[selectedCaseView.toLocaleUpperCase() as keyof ICASES_LOCAL_STORAGE_KEYS].selectedPage,
        pageSize: forDealerEditPage ? LOCAL_STORAGE_KEYS.DEALERS.pageSize : LOCAL_STORAGE_KEYS[selectedCaseView.toLocaleUpperCase() as keyof ICASES_LOCAL_STORAGE_KEYS].pageSize
    })

    if (!hasPermission(DcsPermissions.ViewRetentionCasesView)) {
        caseViews = caseViews.filter((r) => r.text != 'Retention Cases')
    }
    if (!hasPermission(DcsPermissions.ViewCSCasesView)) {
        caseViews = caseViews.filter((r) => r.text != 'CS Cases')
        caseViews = caseViews.filter((r) => r.text != 'CS Campaigns')
        caseViews = caseViews.filter((r) => r.text != 'CS Escalations')
    }
    if (!hasPermission(DcsPermissions.ViewDataCasesView)) {
        caseViews = caseViews.filter((r) => r.text != 'Data Cases')
    }
    if (!hasPermission(DcsPermissions.ViewWebCasesView)) {
        caseViews = caseViews.filter((r) => r.text != 'Web Cases')
        caseViews = caseViews.filter((r) => r.text != 'Web Setup Cases')
        caseViews = caseViews.filter((r) => r.text != 'Lazarus Cases')
    }
    if (!hasPermission(DcsPermissions.ViewDomainCasesView)) {
        caseViews = caseViews.filter((r) => r.text != 'Domain Cases')
    }
    if (!hasPermission(DcsPermissions.ViewDBAccessCasesView)) {
        caseViews = caseViews.filter((r) => r.text != 'DB Access Cases')
    }

    if (!hasPermission(DcsPermissions.ViewSEOCasesView)) {
        caseViews = caseViews.filter((r) => r.text != 'SEO Cases')
    }
    if (!hasPermission(DcsPermissions.ViewSalesCasesView)) {
        caseViews = caseViews.filter((r) => r.text != 'Sales Cases')
    }

    if (!hasPermission(DcsPermissions.ViewQACasesView)) {
        caseViews = caseViews.filter((r) => r.text != 'QA Cases')
    }
    if (!hasPermission(DcsPermissions.ViewAccountingCasesView)) {
        caseViews = caseViews.filter((r) => r.text != 'Accounting Cases')
    }
    if (!hasPermission(DcsPermissions.ViewFrazerOneCasesView)) {
        caseViews = caseViews.filter((r) => r.text != 'FrazerOne')
    }

    const [selectedCaseAction, setSelectedCaseAction] = useState({
        value: { text: 'Actions', id: 0 }
    })



    function getCaseViewVal(caseVal: string) {
        let val = caseVal.replace(/\s/g, '') as keyof typeof CaseViewEnum
        return val
    }

    const handleBulkDealerChange = (e: IDealerChangedEvent) => {
        setBulkSelectedDealerId(e.dealerId)
        caseFormRef.current?.valueSetter('dealerId', e.dealerId)
    }

    const handleBulkDealerClear = () => {
        setBulkSelectedDealerId(undefined)
        caseFormRef.current?.valueSetter('dealerId', undefined)
    }

    const submit = async (values: BulkUpdateCases) => {
        try {
            setCaseModalLoading(true)
            await startLoadingAction()
            if (!selectedIds || selectedIds.length === 0)
                return

            caseFormRef.current?.values.caseReasonId && !caseFormRef.current?.values.caseSubReasonId ? caseFormRef.current.values.caseSubReasonId = 0 : null
            caseFormRef.current?.values.caseStatusId && !caseFormRef.current?.values.caseSubStatusId ? caseFormRef.current.values.caseSubStatusId = 0 : null
            const dto: BulkUpdateCases = new BulkUpdateCases(
                {
                    bulkIds: new BulkIdsDto(
                        { ids: selectedIds }
                    ),
                    //set assignedToUser
                    assignedToUserId: caseFormRef.current?.values.assignedToUserId && caseFormRef.current?.values.assignedToUserId != 0 ? caseFormRef.current?.values.assignedToUserId : undefined,
                    //set note
                    note: caseFormRef.current?.values.note ? caseFormRef.current.values.note : undefined,
                    private: caseFormRef.current?.values.private ? caseFormRef.current.values.private : undefined,
                    //set delete/approve
                    deleteCase: caseFormRef.current?.values.deleteCase ? caseFormRef.current.values.deleteCase : undefined,
                    approveCase: caseFormRef.current?.values.approveCase ? caseFormRef.current.values.approveCase : undefined,
                    //Reason/SubReason
                    caseReasonId: caseFormRef.current?.values.caseReasonId ?? undefined,
                    caseSubReasonId: caseFormRef.current?.values.caseSubReasonId ?? undefined,
                    //Status/SubStatus 
                    caseStatusId: caseFormRef.current?.values.caseStatusId ?? undefined,
                    caseSubStatusId: caseFormRef.current?.values.caseSubStatusId ?? undefined,
                    dealerId: bulkSelectedDealerId,
                })

            let result = await V1ApiCasesClient.bulkUpdateCases(dto)
            if (result.success) {
                toast.success('Case was successfully updated.')
                setShowCaseModal(false)
            }
        } catch (e) {
            toast.responseErrors(e)
        }
        finally {
            setSelectedIds([])
            setBulkSelectedDealerId(undefined)
            caseFormRef.current?.resetForm()
            gridRef.current?.fetch()
            await endLoadingAction()
            setCaseModalLoading(false)
        }
    }



    const fetchCaseReassignmentUserData = async () => {
        try {
            const activeCaseUsers = await V1ApiUsersClient.getUsers(DeletedStateEnum.Active, undefined, undefined, undefined, 1, 100)
            setCaseUserOptions(activeCaseUsers.data.map((item: UsersDto) => ({ id: item.usersId, text: item.firstName + ' ' + item.lastName } as IDropDownValue)))
        }
        catch (e) {
            toast.responseErrors(e)
        }
    }


    const fetchCaseReasonData = async () => {
        try {
            const reasonsData = await V1ApiCasesClient.getCaseReasons(undefined, undefined, true, 1, 100)
            let reasonsMapped = reasonsData.data.map((item: CaseReasonDto) => ({ id: item.caseReasonId, text: item.caseReason1 } as IDropDownValue))

            let reasonsArr: Array<IDropDownValue> = new Array(({ id: 0, text: '' } as IDropDownValue))
            reasonsArr.push(...reasonsMapped)

            setReasonOptions(reasonsArr)
        }
        catch (e) {
            toast.responseErrors(e)
        }
    }


    useEffect(() => {
        if (selectedCaseAction.value.id == 1) {
            setShowCaseModal(true)

        }

        if (selectedCaseAction.value.id == 2) {
            setShowCaseModal(true)

        }

        if (selectedCaseAction.value.id == 3) {
            setCaseModalLoading(true)
            fetchCaseReassignmentUserData()
            setCaseModalLoading(false)
        }

        if (selectedCaseAction.value.id == 4) {
            setShowCaseModal(true)
        }
        if (selectedCaseAction.value.id == 5) {
            caseFormRef.current?.valueSetter('deleteCase', true)
            setShowCaseModal(true)
        }

        if (selectedCaseAction.value.id == 6) {
            caseFormRef.current?.valueSetter('approveCase', true)
            setShowCaseModal(true)
        }

        if (selectedCaseAction.value.id == 7) {
            setShowCaseModal(true)
        }

        setLoaded(true)

    }, [selectedCaseAction.value.id])

    const PastDueCell = (props: GridCellProps) => {
        let item = props.dataItem as CasesViewDto
        return (
            <td className='text-center'>
                {item.pastDueInvoiceTotal && item.pastDueInvoiceTotal > 0 ? numberToCurrency(item.pastDueInvoiceTotal, 2) : ''}
            </td>
        )
    }

    const OpenInvoiceTotalCell = (props: GridCellProps) => {
        let item = props.dataItem as CasesViewDto
        return (
            <td className='text-center'>
                {item.openInvoiceTotal && item.openInvoiceTotal > 0 ? numberToCurrency(item.openInvoiceTotal, 2) : ''}
            </td>
        )
    }

    const SendDocCell = (props: GridCellProps) => {
        let item = props.dataItem as CasesViewDto
        return item.dealerId ?
            <td className='text-center '  >
                <a target='_blank' rel='noopener noreferrer' style={{ textDecoration: 'underline', color: '#0d6efd' }} href={`${process.env.REACT_APP_OLD_SYS_ADMIN_URL}/web/Echosign/SendEchoSignDoc.aspx?dealerid=${item.dealerId}`}>Send Doc</a>
            </td>
            : <td></td>
    }

    const ReceivePaymentCell = (props: GridCellProps) => {
        let item = props.dataItem as CasesViewDto
        return item.dealerId ?
            <td className='text-center ' >
                <a target='_blank' style={{ textDecoration: 'underline', color: '#0d6efd' }} rel='noopener noreferrer' href={`${process.env.REACT_APP_OLD_SYS_ADMIN_URL}/web/accounting/AddInvoicePayment.aspx?dealerid=${item.dealerId}`}>Receive Payment</a>
            </td>
            : <td></td>
    }

    const CaseIdCell = (props: GridCellProps) => {

        return (
            <td className='text-center' style={{ width: '80px' }} title='Edit Case in a New Tab' onClick={(e) => e.stopPropagation()}>
                <a target='_blank' href={`/cases/edit/${props.dataItem.caseId}`} >
                    {props.dataItem.caseId}
                </a>
            </td>
        )
    }

    const DealerIdCell = (props: GridCellProps) => {

        return (
            <td className='text-center cursor-pointer' style={{ width: '40px' }} title='Edit Dealer in a New Tab' onClick={(e) => e.stopPropagation()}>
                <a target='_blank' href={`${process.env.REACT_APP_OLD_SYS_ADMIN_URL}/web/Dealers/EditDealer.aspx?id=${props.dataItem.dealerId}`} >
                    {props.dataItem.dealerId}
                </a>
            </td>
        )
    }

    const accountingColumns: ICustomColumn[] = [
        { field: 'assignedToUser', title: 'Owner', filterType: 'text', width: '150px' },
        { field: 'age', title: 'Age', filterType: 'date', className: 'text-center', width: '50px' },
        { field: 'dealerName', title: 'Dealer Name', filterType: 'text', width: '250px' },
        { field: 'dealerId', title: 'DID', filterType: 'numeric', cell: DealerIdCell, width: '70px' },
        { field: 'caseId', title: 'Case ID', sortable: true, cell: CaseIdCell, filterType: 'numeric', width: '80px' },
        { field: 'subject', title: 'Subject', filterType: 'text', width: '150px' },
        { field: 'note', title: 'Notes', filterType: 'text', width: '350px' },
        { field: 'timeZone', title: 'TZ', width: '45px', filterType: 'text' },
        { field: 'pastDueInvoiceTotal', cell: PastDueCell, title: 'Past Due', sortable: true, filterType: 'numeric', width: '85px' },
        { field: 'openInvoiceTotal', cell: OpenInvoiceTotalCell, title: 'Open', sortable: true, filterType: 'numeric', width: '85px' },
        { field: 'dealerId', cell: ReceivePaymentCell, title: 'Receive Payment', filterType: 'text', width: '150px' },
        { field: 'dealerId', cell: SendDocCell, title: 'Send Doc ', filterType: 'text', width: '75px' },
        { field: 'caseId', title: 'Select', sortable: false, headerClassName: 'headerClass', cellType: GridCellEnum.MultiSelect, display: hasPermission(DcsPermissions.EditCase) || (!forDealerEditPage && hasPermission(DcsPermissions.EditCase)) },
    ]

    const retentionColumns: ICustomColumn[] = [
        { field: 'age', title: 'Age', filterType: 'date', className: 'text-center', width: '50px' },
        { field: 'caseId', title: 'Case ID', sortable: true, cell: CaseIdCell, filterType: 'numeric', width: '70px' },
        { field: 'dealerId', title: 'DID', filterType: 'numeric', cell: DealerIdCell, width: '70px' },
        { field: 'dealerName', title: 'Dealer Name', filterType: 'text' },
        { field: 'assignedToUser', title: 'Owner', filterType: 'text', width: '180px' },
        { field: 'subject', title: 'Subject', filterType: 'text', width: '450px' },
        { field: 'note', title: 'Notes', filterType: 'text', width: '350px', className: 'case-note-cell', headerClassName: 'case-note-header' },
        { field: 'caseStatus', title: 'Status', filterType: 'text', width: '165px' },
        { field: 'caseSubStatus', title: 'Sub Status', filterType: 'text', width: '95px' },
        { field: 'dueDate', title: 'Due Date', format: '{0:d}', filterType: 'date', width: '85px' },
        { field: 'timeZone', title: 'TZ', width: '45px', filterType: 'text' },
        { field: 'caseId', title: 'Select', sortable: false, headerClassName: 'headerClass', cellType: GridCellEnum.MultiSelect, display: hasPermission(DcsPermissions.EditCase) || (!forDealerEditPage && hasPermission(DcsPermissions.EditCase)) },
    ]

    const csColumns: ICustomColumn[] = [
        { field: 'age', title: 'Age', filterType: 'date', className: 'text-center', width: '50px' },
        { field: 'lastModifiedDate', title: 'Last Modified', format: '{0:g}', filterType: 'date' },
        { field: 'caseId', title: 'Case ID', sortable: true, cell: CaseIdCell, filterType: 'numeric', width: '80px' },
        { field: 'dealerId', title: 'DID', filterType: 'numeric', cell: DealerIdCell, width: '70px' },
        { field: 'dealerName', title: 'Dealer Name', filterType: 'text' },
        { field: 'assignedToUser', title: 'Owner', filterType: 'text' },
        { field: 'subject', title: 'Subject', filterType: 'text', width: '250px' },
        { field: 'caseStatus', title: 'Status', filterType: 'text' },
        { field: 'caseReason', title: 'Reason', filterType: 'text' },
        { field: 'caseSubReason', title: 'Sub Reason', filterType: 'text' },
        { field: 'dealerStatus', title: 'Dealer Status', filterType: 'text' },
        { field: 'timeZone', title: 'TZ', width: '45px', filterType: 'text' },
        { field: 'note', title: 'Notes', filterType: 'text', width: '350px', className: 'case-note-cell', headerClassName: 'case-note-header' },
        { field: 'caseId', title: 'Select', sortable: false, headerClassName: 'headerClass', cellType: GridCellEnum.MultiSelect, display: hasPermission(DcsPermissions.EditCase) || (!forDealerEditPage && hasPermission(DcsPermissions.EditCase)) },
    ]

    const dataColumns: ICustomColumn[] = [
        { field: 'age', title: 'Age', filterType: 'date', width: '50px' },
        { field: 'lastModifiedDate', title: 'Last Modified', format: '{0:g}', filterType: 'date' },
        { field: 'caseId', title: 'Case ID', sortable: true, cell: CaseIdCell, filterType: 'numeric', width: '80px' },
        { field: 'dealerId', title: 'DID', filterType: 'numeric', cell: DealerIdCell, width: '70px' },
        { field: 'dealerName', title: 'Dealer Name', filterType: 'text' },
        { field: 'assignedToUser', title: 'Owner', filterType: 'text' },
        { field: 'subject', title: 'Subject', filterType: 'text', width: '350px' },
        { field: 'caseStatus', title: 'Status', filterType: 'text' },
        { field: 'caseReason', title: 'Reason', filterType: 'text' },
        { field: 'caseSubReason', title: 'Sub Reason', filterType: 'text' },
        { field: 'timeZone', title: 'TZ', width: '45px', filterType: 'text' },
        { field: 'caseId', title: 'Select', sortable: false, headerClassName: 'headerClass', cellType: GridCellEnum.MultiSelect, display: hasPermission(DcsPermissions.EditCase) || (!forDealerEditPage && hasPermission(DcsPermissions.EditCase)) },
    ]

    const seoColumns: ICustomColumn[] = [
        { field: 'age', title: 'Age', filterType: 'date', className: 'text-center', width: '50px' },
        { field: 'lastModifiedDate', title: 'Last Modified', format: '{0:g}', filterType: 'date' },
        { field: 'caseId', title: 'Case ID', sortable: true, cell: CaseIdCell, filterType: 'numeric', width: '80px' },
        { field: 'dealerId', title: 'DID', filterType: 'numeric', cell: DealerIdCell, width: '70px' },
        { field: 'dealerName', title: 'Dealer Name', filterType: 'text', width: '250px' },
        { field: 'assignedToUser', title: 'Owner', filterType: 'text' },
        { field: 'subject', title: 'Subject', filterType: 'text', width: '350px' },
        { field: 'note', title: 'Notes', filterType: 'text', width: '350px', className: 'case-note-cell', headerClassName: 'case-note-header' },
        { field: 'caseStatus', title: 'Status', filterType: 'text', width: '120px' },
        { field: 'timeZone', title: 'TZ', width: '45px', filterType: 'text' },
        { field: 'caseId', title: 'Select', sortable: false, headerClassName: 'headerClass', cellType: GridCellEnum.MultiSelect, display: hasPermission(DcsPermissions.EditCase) || (!forDealerEditPage && hasPermission(DcsPermissions.EditCase)) },
    ]

    const domainColumns: ICustomColumn[] = [
        { field: 'age', title: 'Age', filterType: 'date', className: 'text-center', width: '50px' },
        { field: 'lastModifiedDate', title: 'Last Modified', format: '{0:g}', filterType: 'date' },
        { field: 'caseId', title: 'Case ID', sortable: true, cell: CaseIdCell, filterType: 'numeric', width: '80px' },
        { field: 'dealerId', title: 'DID', filterType: 'numeric', cell: DealerIdCell, width: '70px' },
        { field: 'dealerName', title: 'Dealer Name', filterType: 'text' },
        { field: 'assignedToUser', title: 'Owner', filterType: 'text' },
        { field: 'subject', title: 'Subject', filterType: 'text', width: '350px' },
        { field: 'caseStatus', title: 'Status', filterType: 'text' },
        { field: 'caseSubStatus', title: 'Sub Status', filterType: 'text' },
        { field: 'caseReason', title: 'Reason', filterType: 'text' },
        { field: 'caseSubReason', title: 'Sub Reason', filterType: 'text' },
        { field: 'dealerStatus', title: 'Dealer Status', filterType: 'text' },
        { field: 'caseType', title: 'Type', filterType: 'text' },
        { field: 'note', title: 'Notes', filterType: 'text' },
        { field: 'timeZone', title: 'TZ', width: '45px', filterType: 'text' },
        { field: 'caseId', title: 'Select', sortable: false, headerClassName: 'headerClass', cellType: GridCellEnum.MultiSelect, display: hasPermission(DcsPermissions.EditCase) || (!forDealerEditPage && hasPermission(DcsPermissions.EditCase)) },
    ]

    const webSupportColumns: ICustomColumn[] = [
        { field: 'age', title: 'Age', filterType: 'date', className: 'text-center', width: '50px' },
        { field: 'lastModifiedDate', title: 'Last Modified', format: '{0:g}', filterType: 'date' },
        { field: 'caseId', title: 'Case ID', sortable: true, cell: CaseIdCell, filterType: 'numeric', width: '80px' },
        { field: 'dealerId', title: 'DID', filterType: 'numeric', cell: DealerIdCell, width: '70px' },
        { field: 'dealerName', title: 'Dealer Name', filterType: 'text' },
        { field: 'assignedToUser', title: 'Owner', filterType: 'text' },
        { field: 'subject', title: 'Subject', filterType: 'text', width: '350px' },
        { field: 'caseStatus', title: 'Status', filterType: 'text' },
        { field: 'caseReason', title: 'Reason', filterType: 'text' },
        { field: 'caseSubReason', title: 'Sub Reason', filterType: 'text' },
        { field: 'dealerStatus', title: 'Dealer Status', filterType: 'text' },
        { field: 'timeZone', title: 'TZ', width: '45px', filterType: 'text' },
        { field: 'note', title: 'Notes', filterType: 'text' },
        { field: 'caseId', title: 'Select', sortable: false, headerClassName: 'headerClass', cellType: GridCellEnum.MultiSelect, display: hasPermission(DcsPermissions.EditCase) || (!forDealerEditPage && hasPermission(DcsPermissions.EditCase)) },
    ]

    const webSetupColumns: ICustomColumn[] = [
        { field: 'age', title: 'Age', filterType: 'date', className: 'text-center', width: '50px' },
        { field: 'lastModifiedDate', title: 'Last Modified', format: '{0:g}', filterType: 'date' },
        { field: 'caseId', title: 'Case ID', sortable: true, cell: CaseIdCell, filterType: 'numeric', width: '80px' },
        { field: 'dealerId', title: 'DID', filterType: 'numeric', cell: DealerIdCell, width: '70px' },
        { field: 'dealerName', title: 'Dealer Name', filterType: 'text' },
        { field: 'assignedToUser', title: 'Owner', filterType: 'text' },
        { field: 'subject', title: 'Subject', filterType: 'text', width: '350px' },
        { field: 'caseStatus', title: 'Status', filterType: 'text' },
        { field: 'caseReason', title: 'Reason', filterType: 'text' },
        { field: 'caseSubReason', title: 'Sub Reason', filterType: 'text' },
        { field: 'dealerStatus', title: 'Dealer Status', filterType: 'text' },
        { field: 'timeZone', title: 'TZ', width: '45px', filterType: 'text' },
        { field: 'note', title: 'Notes', filterType: 'text' },
        { field: 'caseId', title: 'Select', sortable: false, headerClassName: 'headerClass', cellType: GridCellEnum.MultiSelect, display: hasPermission(DcsPermissions.EditCase) || (!forDealerEditPage && hasPermission(DcsPermissions.EditCase)) },
    ]

    const qaColumns: ICustomColumn[] = [
        { field: 'age', title: 'Age', filterType: 'date', className: 'text-center', width: '50px' },
        { field: 'lastModifiedDate', title: 'Last Modified', format: '{0:g}', filterType: 'date' },
        { field: 'caseId', title: 'Case ID', sortable: true, cell: CaseIdCell, filterType: 'numeric', width: '80px' },
        { field: 'dealerId', title: 'DID', filterType: 'numeric', cell: DealerIdCell, width: '70px' },
        { field: 'dealerName', title: 'Dealer Name', filterType: 'text' },
        { field: 'assignedToUser', title: 'Owner', filterType: 'text' },
        { field: 'subject', title: 'Subject', filterType: 'text', width: '350px' },
        { field: 'caseStatus', title: 'Status', filterType: 'text' },
        { field: 'caseReason', title: 'Reason', filterType: 'text' },
        { field: 'caseSubReason', title: 'Sub Reason', filterType: 'text' },
        { field: 'dealerStatus', title: 'Dealer Status', filterType: 'text' },
        { field: 'timeZone', title: 'TZ', width: '45px', filterType: 'text' },
        { field: 'note', title: 'Notes', filterType: 'text', width: '350px', className: 'case-note-cell', headerClassName: 'case-note-header' },
        { field: 'caseId', title: 'Select', sortable: false, headerClassName: 'headerClass', cellType: GridCellEnum.MultiSelect, display: hasPermission(DcsPermissions.EditCase) || (!forDealerEditPage && hasPermission(DcsPermissions.EditCase)) },
    ]

    const lazarusColumns: ICustomColumn[] = [
        { field: 'age', title: 'Age', filterType: 'date', className: 'text-center', width: '50px' },
        { field: 'lastModifiedDate', title: 'Last Modified', format: '{0:g}', filterType: 'date' },
        { field: 'caseId', title: 'Case ID', sortable: true, cell: CaseIdCell, filterType: 'numeric' },
        { field: 'dealerId', title: 'DID', filterType: 'numeric', cell: DealerIdCell },
        { field: 'dealerName', title: 'Dealer Name', filterType: 'text' },
        { field: 'assignedToUser', title: 'Owner', filterType: 'text' },
        { field: 'subject', title: 'Subject', filterType: 'text', width: '350px' },
        { field: 'caseStatus', title: 'Status', filterType: 'text' },
        { field: 'caseReason', title: 'Reason', filterType: 'text' },
        { field: 'caseSubReason', title: 'Sub Reason', filterType: 'text' },
        { field: 'dealerStatus', title: 'Dealer Status', filterType: 'text' },
        { field: 'timeZone', title: 'TZ', width: '45px', filterType: 'text' },
        { field: 'note', title: 'Notes', filterType: 'text', width: '350px', className: 'case-note-cell', headerClassName: 'case-note-header' },
        { field: 'caseId', title: 'Select', sortable: false, headerClassName: 'headerClass', cellType: GridCellEnum.MultiSelect, display: hasPermission(DcsPermissions.EditCase) || (!forDealerEditPage && hasPermission(DcsPermissions.EditCase)) },
    ]

    const frazerOneColumns: ICustomColumn[] = [
        { field: 'age', title: 'Age', filterType: 'date', className: 'text-center', width: '50px' },
        { field: 'lastModifiedDate', title: 'Last Modified', format: '{0:g}', filterType: 'date' },
        { field: 'caseId', title: 'Case ID', sortable: true, cell: CaseIdCell, filterType: 'numeric', width: '80px' },
        { field: 'dealerId', title: 'DID', filterType: 'numeric', cell: DealerIdCell, width: '70px' },
        { field: 'dealerName', title: 'Dealer Name', filterType: 'text' },
        { field: 'assignedToUser', title: 'Owner', filterType: 'text' },
        { field: 'subject', title: 'Subject', filterType: 'text', width: '250px' },
        { field: 'caseStatus', title: 'Status', filterType: 'text' },
        { field: 'caseReason', title: 'Reason', filterType: 'text' },
        { field: 'caseSubReason', title: 'Sub Reason', filterType: 'text' },
        { field: 'dealerStatus', title: 'Dealer Status', filterType: 'text' },
        { field: 'timeZone', title: 'TZ', width: '45px', filterType: 'text' },
        { field: 'note', title: 'Notes', filterType: 'text', width: '350px', className: 'case-note-cell', headerClassName: 'case-note-header' },
        { field: 'caseId', title: 'Select', sortable: false, headerClassName: 'headerClass', cellType: GridCellEnum.MultiSelect, display: hasPermission(DcsPermissions.EditCase) || (!forDealerEditPage && hasPermission(DcsPermissions.EditCase)) },
    ]


    const defaultColumns: ICustomColumn[] = [
        { field: 'age', title: 'Age', filterType: 'date', display: !forDealerEditPage, className: 'case-age-cell', headerClassName: 'case-age-header' },
        { field: 'lastModifiedDate', title: 'Last Modified', format: '{0:g}', filterType: 'date', width: '145px' },
        { field: 'caseId', title: 'Case', sortable: true, cell: CaseIdCell, filterType: 'numeric', width: '85px' },
        { field: 'dealerId', title: 'Dealer ID', filterType: 'numeric', cell: DealerIdCell, display: !forDealerEditPage, width: '85px' },
        { field: 'dealerName', title: 'Dealer Name', filterType: 'text', display: !forDealerEditPage, width: '125px' },
        { field: 'assignedToUser', title: 'Owner', filterType: 'text', width: '150px' },
        { field: 'subject', title: 'Subject', filterType: 'text', className: 'case-subject-cell', headerClassName: 'case-subject-header' },
        { field: 'caseStatus', title: 'Status', filterType: 'text', display: forDealerEditPage || (showClosedCases || showOpenCases) && !(caseView != undefined && caseView == CaseViewEnum.AccountingCases), width: '90px' },
        { field: 'caseReason', title: 'Reason', filterType: 'text', display: !(caseView != undefined && caseView == CaseViewEnum.AccountingCases), width: '150px' },
        { field: 'caseSubReason', title: 'Sub Reason', filterType: 'text', display: !forDealerEditPage && !(caseView != undefined && caseView == CaseViewEnum.AccountingCases), width: '130px' },
        { field: 'casePriority', title: 'Priority', filterType: 'text', display: false },
        { field: 'dealerStatus', title: 'Dealer Status', filterType: 'text', display: !forDealerEditPage && !(caseView != undefined && caseView == CaseViewEnum.AccountingCases), width: '100px' },
        { field: 'timeZone', title: 'TZ', width: '45px', filterType: 'text', display: !forDealerEditPage },
        { display: forDealerEditPage ? false : showNoteCol, field: 'note', title: 'Note', filterType: 'text', className: 'case-note-cell', headerClassName: 'case-note-header' },
        //accounting view fields
        { field: 'phone', title: 'Phone', filterType: 'text', headerClassName: 'text-center', display: caseView != undefined && caseView == CaseViewEnum.AccountingCases, width: '190px' },
        { field: 'pastDueInvoiceTotal', cell: PastDueCell, title: 'Past Due', sortable: true, filterType: 'numeric', width: '85px', display: caseView != undefined && caseView == CaseViewEnum.AccountingCases },
        { field: 'openInvoiceTotal', cell: OpenInvoiceTotalCell, title: 'Open', sortable: true, filterType: 'numeric', width: '85px', display: caseView != undefined && caseView == CaseViewEnum.AccountingCases },
        { field: 'invoiceTypeName', title: 'MOP', sortable: true, filterType: 'text', width: '95px', display: caseView != undefined && caseView == CaseViewEnum.AccountingCases },
        { field: 'dealerId', width: '85px', cell: SendDocCell, title: 'Send Doc ', filterType: 'text', display: caseView != undefined && caseView == CaseViewEnum.AccountingCases },
        { field: 'dealerId', width: '155px', cell: ReceivePaymentCell, title: 'Receive Payment', filterType: 'text', display: caseView != undefined && caseView == CaseViewEnum.AccountingCases },
        //retention view field
        { field: 'dueDate', title: 'Due Date', format: '{0:d}', filterType: 'date', width: '145px', display: caseView != undefined && caseView == CaseViewEnum.RetentionCases },
        { field: 'caseId', title: 'Select', sortable: false, headerClassName: 'headerClass', cellType: GridCellEnum.MultiSelect, display: hasPermission(DcsPermissions.EditCase) || (!forDealerEditPage && hasPermission(DcsPermissions.EditCase)) },
    ]

    const [selectedColumns, setSelectedColumns] = useState<Array<ICustomColumn>>()

    const casesViewTypeChanged = (e: DropDownListChangeEvent) => {
        if (caseFormRef)
            caseFormRef.current?.resetForm()
        let selectedVal = caseViews.find(x => x.text === e.target.value.text)
        selectedVal && selectedVal?.text != '' ? window.history.replaceState(null, '', `/cases?caseView=${getCaseViewVal(selectedVal.text)}`) : null

        if (!selectedVal)
            return
        switch (selectedVal.text) {
            case 'Data Cases': {
                setShowNoteCol(true)
                setShowRoleCol(false)
                setCasesViewType({
                    value: selectedVal
                })
                setSelectedColumns(dataColumns)
                break
            }

            case 'Web Cases': {
                setShowNoteCol(true)
                setShowRoleCol(false)
                setCasesViewType({
                    value: selectedVal
                })
                setSelectedColumns(webSetupColumns)
                break
            }

            case 'Web Setup Cases': {
                setShowNoteCol(true)
                setShowRoleCol(false)
                setCasesViewType({
                    value: selectedVal
                })
                setSelectedColumns(webSupportColumns)
                break
            }

            case 'Retention Cases': {
                setShowNoteCol(true)
                setShowRoleCol(false)
                setCasesViewType({
                    value: selectedVal
                })
                setSelectedColumns(retentionColumns)
                break
            }

            case 'Sales Cases': {
                setShowNoteCol(true)
                setShowRoleCol(false)
                setCasesViewType({
                    value: selectedVal
                })
                setSelectedColumns(defaultColumns)
                break
            }

            case 'DB Access Cases': {
                setShowNoteCol(true)
                setShowRoleCol(false)
                setCasesViewType({
                    value: selectedVal
                })
                setSelectedColumns(defaultColumns)
                break
            }

            case 'CS Escalations': {
                setShowNoteCol(true)
                setShowRoleCol(false)
                setCasesViewType({
                    value: selectedVal
                })
                setSelectedColumns(defaultColumns)
                break
            }

            case 'CS Campaigns': {
                setShowNoteCol(true)
                setShowRoleCol(false)
                setCasesViewType({
                    value: selectedVal
                })
                setSelectedColumns(defaultColumns)
                break
            }

            case 'SEO Cases': {
                setShowNoteCol(true)
                setShowRoleCol(false)
                setCasesViewType({
                    value: selectedVal
                })
                setSelectedColumns(seoColumns)
                break
            }

            case 'Domain Cases': {
                setShowNoteCol(true)
                setShowRoleCol(false)
                setCasesViewType({
                    value: selectedVal
                })
                setSelectedColumns(domainColumns)
                break
            }

            case 'CS Cases': {
                setShowNoteCol(true)
                setShowRoleCol(false)
                setCasesViewType({
                    value: selectedVal
                })
                setSelectedColumns(csColumns)
                break
            }
            case 'QA Cases': {
                setShowNoteCol(true)
                setShowRoleCol(false)
                setCasesViewType({
                    value: selectedVal
                })
                setSelectedColumns(qaColumns)
                break
            }
            case 'Accounting Cases': {
                setShowNoteCol(true)
                setShowRoleCol(false)
                setCasesViewType({
                    value: selectedVal
                })
                setSelectedColumns(accountingColumns)
                break
            }
            case 'Lazarus Cases': {
                setShowNoteCol(true)
                setShowRoleCol(false)
                setCasesViewType({
                    value: selectedVal
                })
                setSelectedColumns(lazarusColumns)
                break
            }
            case 'FrazerOne Cases': {
                setShowNoteCol(true)
                setShowRoleCol(false)
                setCasesViewType({
                    value: selectedVal
                })
                setSelectedColumns(csColumns)
                break
            }
            default: {
                setShowNoteCol(false)
                setShowRoleCol(true)
                setCasesViewType({
                    value: selectedVal
                })
                setSelectedColumns(defaultColumns)
            }
        }

    }
    const defaultAction = { text: 'Action...', id: 0 }

    const getActions = (): Array<IDropDownValue> => {
        let result = [
            { text: 'Action...', id: 0 }
        ]

        if (hasPermission(DcsPermissions.EditCase))
            result = [...result,
            { text: 'Change Case Status', id: 1 },
            { text: 'Change Case Reason', id: 2 },
            { text: 'Reassign Case(s)', id: 3 },
            { text: 'Add Note', id: 4 },
            { text: 'Delete Case(s)', id: 5 },
            { text: 'Approve Case(s)', id: 6 },
            { text: 'Change Dealer', id: 7 },
            ]

        if (!hasPermission(DcsPermissions.AddNoteCase))
            result = result.filter((r) => r.id != 4)
        if (!hasPermission(DcsPermissions.DeleteCase))
            result = result.filter((r) => r.id != 5)
        if (!hasPermission(DcsPermissions.ApproveCase))
            result = result.filter((r) => r.id != 6)

        return result.sort((a, b) => a.id - b.id)
    }
    const actions = getActions()


    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onActionChange = async (event: DropDownListChangeEvent, ids: number[], selectedData: any[] | undefined) => {

        if (ids.length === 0) {
            toast.warn('No Items Selected')
            return
        }

        setSelectedIds(ids)

        switch (event.value.id) {
            case 1: {
                if (hasPermission(DcsPermissions.EditCase)) {
                    setSelectedCaseAction({
                        value: { text: event.value.text, id: event.value.id }
                    })
                    setShowCaseModal(true)
                    setCaseModalErrors([])
                } else {
                    toast.error(DEFAULT_PERMISSION_MESSAGE)
                }
                break
            }

            case 2: {
                if (hasPermission(DcsPermissions.EditCase)) {
                    setSelectedCaseAction({
                        value: { text: event.value.text, id: event.value.id }
                    })
                    setShowCaseModal(true)
                } else {
                    toast.error(DEFAULT_PERMISSION_MESSAGE)
                }
                break
            }

            case 3: {
                if (hasPermission(DcsPermissions.EditCase)) {
                    setSelectedCaseAction({
                        value: { text: event.value.text, id: event.value.id }
                    })
                    setShowCaseModal(true)
                } else {
                    toast.error(DEFAULT_PERMISSION_MESSAGE)
                }
                break
            }

            case 4: {
                if (hasPermission(DcsPermissions.EditCase)) {
                    setSelectedCaseAction({
                        value: { text: event.value.text, id: event.value.id }
                    })
                    setShowCaseModal(true)

                } else {
                    toast.error(DEFAULT_PERMISSION_MESSAGE)
                }
                break
            }

            case 5: {
                if (hasPermission(DcsPermissions.DeleteCase)) {
                    setSelectedCaseAction({
                        value: { text: event.value.text, id: event.value.id }
                    })
                    setShowCaseModal(true)
                }
                else {
                    toast.error(DEFAULT_PERMISSION_MESSAGE)
                }
                break
            }

            case 6: {
                if (hasPermission(DcsPermissions.ApproveCase)) {
                    setSelectedCaseAction({
                        value: { text: event.value.text, id: event.value.id }
                    })
                    setShowCaseModal(true)
                }
                else {
                    toast.error(DEFAULT_PERMISSION_MESSAGE)
                }
                break
            }

            case 7: {
                if (hasPermission(DcsPermissions.EditCase)) {
                    setBulkSelectedDealerId(undefined)
                    setSelectedCaseAction({
                        value: { text: event.value.text, id: event.value.id }
                    })
                    setShowCaseModal(true)
                } else {
                    toast.error(DEFAULT_PERMISSION_MESSAGE)
                }
                break
            }

        }
    }
    let hasViewCasePermission = true
    const fetchCases = async (props: GridGetDataProps): Promise<GetDataResponse> => {
        try {

            startLoadingAction()

            let page = props.paging && props.paging.currentPage ? props.paging.currentPage : 1

            let selected: CaseViewEnum = CaseViewEnum[selectedCaseView]

            if (!hasPermission(DcsPermissions.ViewRetentionCasesView)) {
                caseViews = caseViews.filter((r) => r.text != 'Retention Cases')
                if (caseViewFromQueryOrFilter == getCaseViewVal('Retention Cases'))
                    hasViewCasePermission = false
            }

            if (!hasPermission(DcsPermissions.ViewCSCasesView)) {
                caseViews = caseViews.filter((r) => r.text != 'CS Cases')
                caseViews = caseViews.filter((r) => r.text != 'CS Campaigns')
                caseViews = caseViews.filter((r) => r.text != 'CS Escalations')

                if (caseViewFromQueryOrFilter == getCaseViewVal('CS Cases') || caseViewFromQueryOrFilter == getCaseViewVal('CS Campaigns') || caseViewFromQueryOrFilter == getCaseViewVal('CS Escalations'))
                    hasViewCasePermission = false
            }

            if (!hasPermission(DcsPermissions.ViewDataCasesView)) {
                caseViews = caseViews.filter((r) => r.text != 'Data Cases')
                if (caseViewFromQueryOrFilter == getCaseViewVal('Data Cases'))
                    hasViewCasePermission = false
            }

            if (!hasPermission(DcsPermissions.ViewWebCasesView)) {
                caseViews = caseViews.filter((r) => r.text != 'Web Cases')
                if (caseViewFromQueryOrFilter == getCaseViewVal('Web Cases'))
                    hasViewCasePermission = false
            }

            if (!hasPermission(DcsPermissions.ViewDomainCasesView)) {
                caseViews = caseViews.filter((r) => r.text != 'Domain Cases')
                if (caseViewFromQueryOrFilter == getCaseViewVal('Domain Cases'))
                    hasViewCasePermission = false
            }

            if (!hasPermission(DcsPermissions.ViewDBAccessCasesView)) {
                caseViews = caseViews.filter((r) => r.text != 'DB Access Cases')
                if (caseViewFromQueryOrFilter == getCaseViewVal('DB Access Cases'))
                    hasViewCasePermission = false
            }

            if (!hasPermission(DcsPermissions.ViewSEOCasesView)) {
                caseViews = caseViews.filter((r) => r.text != 'SEO Cases')
                if (caseViewFromQueryOrFilter == getCaseViewVal('SEO Cases'))
                    hasViewCasePermission = false
            }
            if (!hasPermission(DcsPermissions.ViewSalesCasesView)) {
                caseViews = caseViews.filter((r) => r.text != 'Sales Cases')
                if (caseViewFromQueryOrFilter == getCaseViewVal('Sales Cases'))
                    hasViewCasePermission = false
            }

            if (!hasPermission(DcsPermissions.ViewQACasesView)) {
                caseViews = caseViews.filter((r) => r.text != 'QA Cases')
                if (caseViewFromQueryOrFilter == getCaseViewVal('QA Cases'))
                    hasViewCasePermission = false
            }
            if (!hasPermission(DcsPermissions.ViewAccountingCasesView)) {
                caseViews = caseViews.filter((r) => r.text != 'Accounting Cases')
                if (caseViewFromQueryOrFilter == getCaseViewVal('Accounting Cases'))
                    hasViewCasePermission = false
            }
            if (!hasPermission(DcsPermissions.ViewFrazerOneCasesView)) {
                caseViews = caseViews.filter((r) => r.text != 'FrazerOne Cases')
                if (caseViewFromQueryOrFilter == getCaseViewVal('FrazerOne Cases'))
                    hasViewCasePermission = false
            }

            if (!hasViewCasePermission) {
                toast.error(DEFAULT_PERMISSION_MESSAGE)
                hasViewCasePermission = true
                setCaseViewFromQueryLoaded(true)
                return {
                    data: [],
                    total: 0
                }
            }

            const closedCasesByUserParam = closedCasesByUser === 'true' ? true : closedCasesByUser === 'false' ? false : null

            let parsedStartDate: Date | null = null
            if (closedCasesStartDate) {
                const parsedMoment = moment(closedCasesStartDate, 'YYYY-M-D', true)
                if (parsedMoment.isValid()) {
                    parsedStartDate = parsedMoment.toDate()
                }
            }

            const result = await V1ApiCasesClient.getCasesView(
                selected,
                DeletedStateEnum.Undefined,
                props.search,
                props.sortBy,
                props.sortAscending,
                page,
                props.paging ? props.paging.take : 25,
                true,
                dealerId && dealerId > 0 ? dealerId : null,
                showClosedCases,
                myCases,
                closedCasesByUserParam,
                parsedStartDate,
                closedCasesUser
            )

            if (result) {
                if (caseView) {
                    setCaseViewFromQueryLoaded(true)
                }
                return {
                    data: result.data,
                    total: result.totalItems ?? 0, // page === 1 ? result.totalItems ? result.totalItems : 0 : props.paging?.total ? props.paging.total : 0,
                    page: result.page,
                    pageSize: result.pageSize,
                    skip: result.skip
                }
            }
            else {
                return {
                    data: [],
                    total: 0
                }
            }
        }
        catch (e) {
            toast.responseErrors(e)
            return {
                data: [],
                total: 0
            }
        }
        finally {
            endLoadingAction()
        }
    }

    useEffect(() => {
        if (gridRef.current) {
            gridRef.current.fetch()
        }
    }, [casesViewType, dealerId])

    useEffect(() => {
        switch (caseView) {
            case 'CSCases':
                setSelectedColumns(csColumns)
                break
            case 'DataCases':
                setSelectedColumns(dataColumns)
                break
            case 'RetentionCases':
                setSelectedColumns(retentionColumns)
                break
            case 'WebCases':
                setSelectedColumns(webSetupColumns)
                break
            case 'DomainCases':
                setSelectedColumns(domainColumns)
                break
            case 'CSCampaigns':
                setSelectedColumns(defaultColumns)
                break
            case 'DBAccessCases':
                setSelectedColumns(defaultColumns)
                break
            case 'SEOCases':
                setSelectedColumns(seoColumns)
                break
            case 'CSEscalations':
                setSelectedColumns(defaultColumns)
                break
            case 'SalesCases':
                setSelectedColumns(defaultColumns)
                break
            case 'QACases':
                setSelectedColumns(qaColumns)
                break
            case 'AccountingCases':
                setSelectedColumns(accountingColumns)
                break
            case 'FrazerOneCases':
                setSelectedColumns(frazerOneColumns)
                break
            default:
                setSelectedColumns(defaultColumns)
                break
        }
    }, [])


    const onCaseFormCancel = (event: React.MouseEvent<HTMLElement> | undefined) => {
        setReasonOptions(undefined)
        setSubReasonOptions(undefined),
            caseFormRef.current?.resetForm()
        setReasonOptions(undefined)
        setSubReasonOptions(undefined)
        setReasonOptions([])
        setStatusOptions([])
        setBulkSelectedDealerId(undefined)
        setShowCaseModal(false)
    }

    const [statusId, setStatusId] = useState<number | undefined | null>(undefined)
    const [reasonId, setReasonId] = useState<number | undefined | null>(undefined)


    return (
        <div id='CasesGrid'>
            {!hideCaseActions &&
                <PageHeader title={casesViewType.value.text}>
                    <div hidden={!hasPermission(DcsPermissions.AddCase)}>
                        <AddButton className='btn-lg btn-primary btnWidth mb-2 mb-md-0' onClick={() => navigate('/cases/add')}>
                            Add Case
                        </AddButton>
                    </div>
                </PageHeader>
            }

            {!forDealerEditPage &&
                <Form
                    ref={caseViewFormRef}
                    ignoreModified={true}
                    initialValues={defaultValues}
                    render={(formRenderProps: FormRenderProps) => (
                        <FormElement>
                            <Row>
                                <Col xs={12} className={useSimpleSearch == true ? '' : 'my-0'}>
                                    <div className='bg-light border p-3' hidden={myCases}>
                                        <Row className='labelMargin'>
                                            <Col md={6} className='mb-3 mb-md-0'>
                                                <Field
                                                    inputPrefix={'Filter By:'}
                                                    name='caseView'
                                                    textField='text'
                                                    dataItemKey='id'
                                                    data={caseViews}
                                                    value={casesViewType.value}
                                                    component={FormDropDownList}
                                                    onChange={casesViewTypeChanged}
                                                />
                                            </Col>

                                        </Row>
                                    </div>
                                </Col>
                            </Row>
                        </FormElement>
                    )}
                />
            }
            <Row>
                <Col xs={12} className='my-3 cases-grid-page'>
                    {selectedColumns &&


                        <SAGrid
                            features={{
                                pageable: true,
                                sortable: true,
                                reorderColumns: true,
                                title,
                                simpleSearch: useSimpleSearch,
                                resizable: true,
                            }}
                            identifierPropertyName='caseId'
                            columns={selectedColumns}
                            key={selectedColumns ? selectedColumns.map(c => c.field).join(',') : 'cases-grid'}
                            ref={gridRef}
                            defaultSort={[{ field: 'lastModifiedDate', dir: 'desc' }]}
                            getData={fetchCases}
                            action={
                                !hideCaseActions && hasPermission(DcsPermissions.EditCase)
                                    ? {
                                        default: defaultAction,
                                        items: actions,
                                        handler: onActionChange,
                                        sendSelectedData: true,
                                        popupWidth: '425px',
                                    }
                                    : undefined
                            }
                            onRowClick={(e: { dataItem: { caseId: number } }) => showOpenCases == true && forDealerEditPage == true && showClosedCases == false ? window.open(`/cases/edit/${e.dataItem.caseId}`, '_blank') : navigate(`/cases/edit/${e.dataItem.caseId}`)}
                            localStorageNames={gridLocalStorageNames}
                        />

                    }
                </Col>
            </Row>

            {showCaseModal ?
                <ConfirmModal
                    className='modal-lg'
                    show={showCaseModal}
                    errors={caseModalErrors}
                    title='Update Case(s)'
                    onCancel={(e) => onCaseFormCancel(e)}
                    onSave={(e) => {
                        let keyPress = (e as unknown as KeyboardEvent).key
                        if (keyPress === 'Enter') {
                            return
                        } else {
                            caseFormRef.current?.onSubmit(e)
                        }
                    }}
                    loading={caseModalLoading}
                    disabled={caseModalLoading}
                    content={
                        <Form
                            ref={caseFormRef}
                            onSubmit={(e) => submit(e as BulkUpdateCases)}
                            ignoreModified={true}
                            initialValues={bulkUpdateCasesDto}
                            key={bulkUpdateCasesDto ? JSON.stringify(bulkUpdateCasesDto) : undefined}
                            render={(formRenderProps) => (
                                <FormElement>
                                    <Card bg='light' className='shadow-sm'>
                                        <Card.Body>
                                            <Row>
                                                {selectedCaseAction.value.id == 1 ?
                                                    <Col md={12}>
                                                        <Field
                                                            component={FormTypeComboBox}
                                                            name='caseStatusSelection'
                                                            label='Status:'
                                                            required
                                                            validator={RequiredValidator}
                                                            placeholder='Select'
                                                            typeEnum={TypeEnum.caseStatus}
                                                            //defaultId={1}
                                                            formRef={caseFormRef}
                                                            formIdName='caseStatusId'
                                                            formTextName='caseStatus1'
                                                            onChange={(e) => {
                                                                const value = e.value as IDropDownValue
                                                                if (loaded) {
                                                                    setStatusId(value && value.id ? value.id : undefined)
                                                                    //Clear out substatus
                                                                    caseFormRef.current?.valueSetter('caseSubStatus1', undefined)
                                                                    caseFormRef.current?.valueSetter('caseSubStatusId', undefined)
                                                                    caseFormRef.current?.valueSetter('caseSubStatusSelection', undefined)
                                                                }
                                                            }}


                                                        />

                                                    </Col> : <></>}
                                                {selectedCaseAction.value.id == 1 ?
                                                    <Col md={12}>
                                                        <Field
                                                            component={FormTypeComboBox}
                                                            name='caseSubStatusSelection'
                                                            label='Sub Status:'
                                                            disabled={!statusId}
                                                            placeholder='Select'
                                                            typeParameterId={statusId ? statusId : undefined}
                                                            typeEnum={TypeEnum.caseSubStatus}
                                                            defaultId={0}
                                                            formRef={caseFormRef}
                                                            formIdName='caseSubStatusId'
                                                            formTextName='caseSubStatus1'
                                                        />
                                                    </Col> : <></>}
                                                {selectedCaseAction.value.id == 2 ?

                                                    <Col md={12}>
                                                        <Field
                                                            component={FormCustomField}
                                                            name='caseReason1'
                                                            label='Reason:'
                                                            required
                                                            validator={RequiredValidator}
                                                            onChange={(value: IDropDownValue) => {
                                                                caseFormRef.current?.valueSetter('caseReason1', value && value.text ? value.text : undefined)
                                                                caseFormRef.current?.valueSetter('caseReasonId', value && value.id ? value.id : undefined)
                                                                setReasonId(value && value.id ? value.id : undefined)
                                                                //Clear out substatus
                                                                caseFormRef.current?.valueSetter('caseSubReason1', undefined)
                                                                caseFormRef.current?.valueSetter('caseSubReasonId', undefined)

                                                            }}
                                                            children={
                                                                <TypeComboBox
                                                                    placeholder='Select'
                                                                    typeEnum={TypeEnum.caseReason}
                                                                />
                                                            }
                                                        />
                                                    </Col> : <></>}
                                                {selectedCaseAction.value.id == 2 ?
                                                    <Col md={12}>
                                                        <Field
                                                            component={FormCustomField}
                                                            name='caseSubReason1'
                                                            label='Sub Reason:'
                                                            disabled={!reasonId}
                                                            onChange={(value: IDropDownValue) => {
                                                                caseFormRef.current?.valueSetter('caseSubReason1', value && value.text ? value.text : undefined)
                                                                caseFormRef.current?.valueSetter('caseSubReasonId', value && value.id ? value.id : undefined)
                                                            }}
                                                            children={
                                                                <TypeComboBox
                                                                    placeholder=''
                                                                    typeParameterId={reasonId ? reasonId : undefined}
                                                                    typeEnum={TypeEnum.caseSubReason}
                                                                />
                                                            }
                                                        />
                                                    </Col> : <></>}
                                                {selectedCaseAction.value.id == 3 ?
                                                    <Col md={12}>

                                                        <Field
                                                            component={FormTypeComboBox}
                                                            name='firstNameSelection'
                                                            label='Assigned To:'
                                                            placeholder='Select'
                                                            typeEnum={TypeEnum.activeUser}
                                                            defaultId={0}
                                                            formRef={caseFormRef}
                                                            formIdName='assignedToUserId'
                                                            formTextName='firstName'
                                                        />
                                                    </Col> : <></>}
                                                {selectedCaseAction.value.id == 4 ?
                                                    <Col md={12}>
                                                        <Field
                                                            label='Note:'
                                                            name='note'
                                                            component={FormTextArea}
                                                            disabled={loading}
                                                            required
                                                            rows={10}
                                                        />
                                                        <Row>

                                                            <Col md={3}>
                                                                <Field
                                                                    component={FormSwitch}
                                                                    label='Private?:'
                                                                    name='private'
                                                                    onLabel='YES'
                                                                    offLabel='NO'
                                                                    id='private'
                                                                />
                                                            </Col>

                                                        </Row>
                                                    </Col>
                                                    : <></>}
                                                {selectedCaseAction.value.id == 5 ?
                                                    <Col md={12}>
                                                        <Row>
                                                            <span>Are you sure you want to delete {selectedIds.length} case(s)?</span>
                                                        </Row>
                                                    </Col> : <></>
                                                }
                                                {selectedCaseAction.value.id == 6 ?
                                                    <Col md={12}>
                                                        <Row>
                                                            <span>Are you sure you want to approve {selectedIds.length} case(s)?</span>
                                                        </Row>
                                                    </Col> : <></>
                                                }
                                                {selectedCaseAction.value.id == 7 ?
                                                    <Col md={12}>
                                                        <Field
                                                            className='form-control'
                                                            dataItemKey='id'
                                                            textField='text'
                                                            name='dealerId'
                                                            label='Dealer:'
                                                            required
                                                            validator={RequiredValidator}
                                                            dealerId={bulkSelectedDealerId ?? 0}
                                                            component={DealerSelect}
                                                            onDealerCleared={handleBulkDealerClear}
                                                            onDealerChanged={handleBulkDealerChange}
                                                        />
                                                    </Col> : <></>}
                                            </Row>
                                        </Card.Body>
                                    </Card>
                                </FormElement>
                            )}
                        />
                    }
                /> : <></>}
        </div>
    )
}

CasesGrid.defaultProps = {
    forDealerEditPage: false,
    showOpenCases: true,
    hideCaseActions: false,
    useSimpleSearch: true
}

export default CasesGrid